// src/components/Reports/ReportKit.jsx
// ======================================================================
// مكونات موحّدة لكل التقارير: الترويسة + بطاقات الإجماليات + الفلاتر + الفترة
//
// الاستخدام في أي تقرير:
//   const range = useReportDateRange();            // اليوم افتراضيًا
//   <ReportPage title="..." icon={<Icon/>} period={range} actions={[...]}
//               stats={[...]} filters={<ReportFilters range={range} ...>...</ReportFilters>}>
//     الجدول
//   </ReportPage>
// ======================================================================

import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import axios from '../../axios';
import { getAllowedBranchIds } from '../../utils/tokenUtils';
import {
  Box, Paper, Typography, Button, TextField, MenuItem, InputAdornment,
  Chip, IconButton, Tooltip, Collapse, CircularProgress, useMediaQuery,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';

// ───────────────────────── ألوان الهوية (مكان واحد لكل النظام) ─────────────────────────
// BRAND يحدد لون الترويسات والأزرار والقوائم وشاشة الدخول في كل النظام.
// لتغيير اللون: اجعل BRAND يساوي أحد الأطقم أدناه (أو أضف طقمًا جديدًا بنفس المفاتيح)

/** النيلي — اللون الأصلي للنظام (المعتمد حاليًا) */
export const INDIGO_BRAND = {
  primary: '#4F46E5',
  primaryDark: '#4338CA',
  primaryDeep: '#3730A3',
  primaryLight: '#818CF8',
  primarySoft: '#EEF2FF',
  secondary: '#10B981',
  secondaryDark: '#059669',
  secondarySoft: '#ECFDF5',
  headerGradient: 'linear-gradient(120deg, #4338ca 0%, #4f46e5 45%, #6366f1 100%)',
  headerShadow: '0 18px 40px -22px rgba(67,56,202,.75)',
  headerGlow: 'rgba(16,185,129,.35)',
  buttonGradient: 'linear-gradient(90deg, #4F46E5 0%, #6366F1 100%)',
  buttonGradientHover: 'linear-gradient(90deg, #3730A3 0%, #4F46E5 100%)',
  accentStrip: 'linear-gradient(90deg, #4F46E5 0%, #10B981 100%)',
  ring: 'rgba(79,70,229,.14)',
};

/** برقوقي مستوحى من Odoo — محفوظ كخيار */
export const ODOO_BRAND = {
  primary: '#714B67',
  primaryDark: '#563A50',
  primaryDeep: '#3F2A3B',
  primaryLight: '#8F6A86',
  primarySoft: '#F4EEF3',
  secondary: '#017E84',
  secondaryDark: '#01666B',
  secondarySoft: '#E6F4F4',
  headerGradient: 'linear-gradient(120deg, #4A3045 0%, #714B67 55%, #87607C 100%)',
  headerShadow: '0 18px 40px -22px rgba(86,58,80,.75)',
  headerGlow: 'rgba(1,126,132,.42)',
  buttonGradient: 'linear-gradient(90deg, #714B67 0%, #87607C 100%)',
  buttonGradientHover: 'linear-gradient(90deg, #563A50 0%, #714B67 100%)',
  accentStrip: 'linear-gradient(90deg, #714B67 0%, #017E84 100%)',
  ring: 'rgba(113,75,103,.14)',
};

export const BRAND = INDIGO_BRAND;

// ───────────────────────── الألوان الموحّدة ─────────────────────────
export const REPORT_COLORS = {
  primary: { main: BRAND.primary, soft: BRAND.primarySoft },
  info:    { main: '#0284c7', soft: '#e0f2fe' },
  warning: { main: '#d97706', soft: '#fef3c7' },
  error:   { main: '#e11d48', soft: '#ffe4e6' },
  success: { main: '#059669', soft: '#d1fae5' },
  slate:   { main: '#475569', soft: '#f1f5f9' },
};

const BORDER = '#e5e7eb';
const DATE_FMT = 'YYYY-MM-DD';

export const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    bgcolor: '#f8fafc',
    fontSize: 14,
    transition: 'background-color .15s',
    '&:hover': { bgcolor: '#fff' },
    '&:hover fieldset': { borderColor: REPORT_COLORS.primary.main },
    '&.Mui-focused': { bgcolor: '#fff' },
  },
  '& .MuiInputLabel-root': { fontSize: 14 },
};

const dateInputSx = {
  ...fieldSx,
  '& input': { direction: 'ltr', textAlign: 'right', fontFamily: 'inherit' },
};

// ───────────────────────── الفترات الجاهزة ─────────────────────────
// بداية الربع بدون إضافة quarterOfYear لـ dayjs
const quarterStart = (d) => d.month(Math.floor(d.month() / 3) * 3).startOf('month');

export const DATE_PRESETS = [
  { key: 'today',     label: 'اليوم',        range: () => [dayjs(), dayjs()] },
  { key: 'yesterday', label: 'أمس',          range: () => { const d = dayjs().subtract(1, 'day'); return [d, d]; } },
  { key: 'week',      label: 'هذا الأسبوع',  range: () => [dayjs().startOf('week'), dayjs()] },
  { key: 'month',     label: 'هذا الشهر',    range: () => [dayjs().startOf('month'), dayjs()] },
  { key: 'lastMonth', label: 'الشهر الماضي', range: () => { const m = dayjs().subtract(1, 'month'); return [m.startOf('month'), m.endOf('month')]; } },
  { key: 'quarter',   label: 'هذا الربع',    range: () => [quarterStart(dayjs()), dayjs()] },
  { key: 'lastQuarter', label: 'الربع الماضي', range: () => { const s = quarterStart(dayjs()).subtract(3, 'month'); return [s, s.add(2, 'month').endOf('month')]; } },
  { key: 'year',      label: 'هذه السنة',    range: () => [dayjs().startOf('year'), dayjs()] },
  { key: 'lastYear',  label: 'السنة الماضية', range: () => { const y = dayjs().subtract(1, 'year'); return [y.startOf('year'), y.endOf('year')]; } },
];

const presetRange = (key) => {
  const p = DATE_PRESETS.find(x => x.key === key) ?? DATE_PRESETS[0];
  const [from, to] = p.range();
  return { from: from.format(DATE_FMT), to: to.format(DATE_FMT) };
};

/**
 * فترة التقرير — تبدأ بتاريخ اليوم افتراضيًا (أو أي فترة جاهزة تمررها)
 * ترجع: { dateFrom, dateTo, setDateFrom, setDateTo, applyPreset, activePreset, reset, label }
 */
export function useReportDateRange(defaultPreset = 'today') {
  const [range, setRange] = useState(() => presetRange(defaultPreset));

  const setDateFrom = (v) => setRange(r => (!v ? r : { from: v, to: v > r.to ? v : r.to }));
  const setDateTo = (v) => setRange(r => (!v ? r : { to: v, from: v < r.from ? v : r.from }));
  const applyPreset = (key) => setRange(presetRange(key));
  const reset = () => setRange(presetRange(defaultPreset));

  const activePreset = DATE_PRESETS.find(p => {
    const r = presetRange(p.key);
    return r.from === range.from && r.to === range.to;
  })?.key ?? null;

  return {
    dateFrom: range.from,
    dateTo: range.to,
    setDateFrom,
    setDateTo,
    applyPreset,
    activePreset,
    reset,
    label: formatPeriod(range.from, range.to, activePreset),
  };
}

export function formatPeriod(from, to, presetKey) {
  const f = dayjs(from).format('DD/MM/YYYY');
  const t = dayjs(to).format('DD/MM/YYYY');
  const presetLabel = DATE_PRESETS.find(p => p.key === presetKey)?.label;
  const dates = from === to ? f : `${f} – ${t}`;
  return presetLabel ? `${presetLabel} · ${dates}` : dates;
}

// ───────────────────────── تنسيق المبالغ ─────────────────────────
export const fmtMoney = (v, dec = 2) =>
  Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: dec, maximumFractionDigits: dec });

export const fmtNumber = (v) => Number(v || 0).toLocaleString('en-US');

// ───────────────────────── الفروع المسموحة ─────────────────────────
/**
 * يجلب فروع الشركة مع تطبيق صلاحيات المستخدم (القائمة الفارغة = كل الفروع)
 * ترجع: { branches, branchOptions, defaultBranch, ready }
 *   defaultBranch: فرع المستخدم إن كان مقيّدًا، أو "" (الكل) إن كان له كل الفروع
 */
export function useAllowedBranches(companyId) {
  const [state, setState] = useState({ branches: [], defaultBranch: '', ready: false });

  useEffect(() => {
    if (!companyId) return;
    let alive = true;
    const myBranchId = parseInt(localStorage.getItem('branchId'), 10);

    axios.get(`/Branch/by-company/${companyId}`)
      .then(r => {
        if (!alive) return;
        const all = Array.isArray(r.data) ? r.data : [];
        const allowed = getAllowedBranchIds();

        if (allowed && allowed.length > 0) {
          const list = all.filter(b => allowed.includes(b.id));
          const def = list.some(b => b.id === myBranchId) ? myBranchId : list[0]?.id;
          setState({ branches: list, defaultBranch: def ? String(def) : '', ready: true });
        } else {
          setState({ branches: all, defaultBranch: '', ready: true });
        }
      })
      .catch(() => alive && setState(s => ({ ...s, ready: true })));

    return () => { alive = false; };
  }, [companyId]);

  return {
    ...state,
    branchOptions: state.branches.map(b => ({ value: String(b.id), label: b.nameAr || b.name })),
    branchName: (id) => {
      const b = state.branches.find(x => String(x.id) === String(id));
      return b ? (b.nameAr || b.name) : null;
    },
  };
}

// ───────────────────────── الصفحة الكاملة ─────────────────────────
/**
 * @param title     عنوان التقرير
 * @param subtitle  وصف قصير (اختياري)
 * @param icon      أيقونة MUI
 * @param period    ناتج useReportDateRange — يُعرض كشارة في الترويسة
 * @param badges    شارات إضافية: [{ label, icon }] مثل الفرع
 * @param actions   أزرار الترويسة: [{ label, icon, onClick, primary, disabled }]
 * @param stats     بطاقات الإجماليات: [{ label, value, icon, color }]
 * @param filters   عنصر <ReportFilters/>
 */
export function ReportPage({ title, subtitle, icon, period, badges = [], actions = [], stats, filters, children, brand }) {
  return (
    <Box dir="rtl" sx={{ px: { xs: 1, sm: 2, md: 3 }, py: { xs: 1, md: 2 }, '@media print': { display: 'none' } }}>
      <ReportHeader
        title={title}
        subtitle={subtitle}
        icon={icon}
        badges={[
          ...(period ? [{ label: period.label, icon: <CalendarMonthIcon /> }] : []),
          ...badges,
        ]}
        actions={actions}
        brand={brand}
      />
      {stats?.length > 0 && <ReportStatCards items={stats} />}
      {filters}
      {children}
    </Box>
  );
}

// ───────────────────────── الترويسة ─────────────────────────
/** brand (اختياري): ألوان مختلفة لصفحة واحدة فقط — مثل INDIGO_BRAND */
export function ReportHeader({ title, subtitle, icon, badges = [], actions = [], brand }) {
  const B = brand ? { ...BRAND, ...brand } : BRAND;
  return (
    <Paper
      elevation={0}
      sx={{
        position: 'relative', overflow: 'hidden', mb: 1.5,
        borderRadius: '16px', color: '#fff',
        px: { xs: 1.75, sm: 2.25, md: 2.5 }, py: { xs: 1.5, sm: 1.75 },
        background: B.headerGradient,
        boxShadow: B.headerShadow,
      }}
    >
      {/* زخرفة خفيفة */}
      <Box aria-hidden sx={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
        background: `
          radial-gradient(420px 220px at 12% -40%, rgba(255,255,255,.22), transparent 70%),
          radial-gradient(360px 200px at 100% 140%, ${B.headerGlow}, transparent 70%)`,
      }} />
      <Box aria-hidden sx={{
        position: 'absolute', inset: 0, pointerEvents: 'none', opacity: .12,
        backgroundImage: 'radial-gradient(rgba(255,255,255,.9) 1px, transparent 1.4px)',
        backgroundSize: '18px 18px',
        WebkitMaskImage: 'linear-gradient(90deg, #000, transparent 60%)',
        maskImage: 'linear-gradient(90deg, #000, transparent 60%)',
      }} />

      <Box sx={{
        position: 'relative', display: 'flex', gap: 2, flexWrap: 'wrap',
        alignItems: { xs: 'flex-start', md: 'center' }, justifyContent: 'space-between',
      }}>
        {/* العنوان */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 }, minWidth: 0 }}>
          {icon && (
            <Box sx={{
              width: { xs: 42, sm: 48 }, height: { xs: 42, sm: 48 }, flexShrink: 0, borderRadius: '13px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              bgcolor: 'rgba(255,255,255,.16)', border: '1px solid rgba(255,255,255,.28)',
              backdropFilter: 'blur(6px)',
              '& svg': { fontSize: { xs: 22, sm: 25 } },
            }}>
              {icon}
            </Box>
          )}
          <Box sx={{ minWidth: 0 }}>
            <Typography component="h1" sx={{ fontWeight: 800, fontSize: { xs: '1.15rem', sm: '1.35rem', md: '1.5rem' }, lineHeight: 1.3 }}>
              {title}
            </Typography>
            {subtitle && (
              <Typography sx={{ opacity: .85, fontSize: { xs: 13, sm: 14 }, mt: .25 }}>{subtitle}</Typography>
            )}
            {badges.length > 0 && (
              <Box sx={{ display: 'flex', gap: .75, flexWrap: 'wrap', mt: .75 }}>
                {badges.map((b, i) => (
                  <Box key={i} sx={{
                    display: 'inline-flex', alignItems: 'center', gap: .6,
                    px: 1.25, py: .4, borderRadius: '999px', fontSize: 13, fontWeight: 600,
                    bgcolor: 'rgba(255,255,255,.15)', border: '1px solid rgba(255,255,255,.22)',
                    '& svg': { fontSize: 16 },
                  }}>
                    {b.icon}{b.label}
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Box>

        {/* الأزرار */}
        {actions.length > 0 && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', width: { xs: '100%', md: 'auto' } }}>
            {actions.map((a, i) => (
              <Button
                key={i}
                onClick={a.onClick}
                disabled={a.disabled}
                startIcon={a.icon}
                sx={{
                  flex: { xs: 1, md: 'none' }, minHeight: 40, px: 2, borderRadius: '11px',
                  fontWeight: 700, fontSize: 14, whiteSpace: 'nowrap',
                  '& .MuiButton-startIcon': { ml: .75, mr: -.25 },
                  ...(a.primary
                    ? { bgcolor: '#fff', color: B.primaryDark, '&:hover': { bgcolor: B.primarySoft } }
                    : {
                        color: '#fff', bgcolor: 'rgba(255,255,255,.12)', border: '1px solid rgba(255,255,255,.3)',
                        '&:hover': { bgcolor: 'rgba(255,255,255,.22)' },
                      }),
                  '&.Mui-disabled': { color: 'rgba(255,255,255,.5)' },
                }}
              >
                {a.label}
              </Button>
            ))}
          </Box>
        )}
      </Box>
    </Paper>
  );
}

// ───────────────────────── بطاقات الإجماليات ─────────────────────────
export function ReportStatCards({ items }) {
  return (
    <Box sx={{
      display: 'grid', gap: { xs: 1, sm: 1.25 }, mb: 1.5,
      gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(auto-fit, minmax(180px, 1fr))' },
    }}>
      {items.map((s, i) => {
        const c = REPORT_COLORS[s.color] ?? REPORT_COLORS.primary;
        const highlight = !!s.highlight;
        return (
          <Paper
            key={i}
            elevation={0}
            sx={{
              p: { xs: 1.25, sm: 1.4 }, borderRadius: '14px', minWidth: 0,
              display: 'flex', alignItems: 'center', gap: 1.5,
              border: `1px solid ${highlight ? c.main : BORDER}`,
              bgcolor: highlight ? c.main : '#fff',
              color: highlight ? '#fff' : 'inherit',
              boxShadow: highlight ? `0 12px 24px -14px ${c.main}` : '0 1px 2px rgba(15,23,42,.04)',
              ...(highlight && { gridColumn: { xs: 'span 2', sm: 'auto' } }),
            }}
          >
            <Box sx={{
              width: { xs: 38, sm: 44 }, height: { xs: 38, sm: 44 }, flexShrink: 0, borderRadius: '12px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              bgcolor: highlight ? 'rgba(255,255,255,.2)' : c.soft,
              color: highlight ? '#fff' : c.main,
              '& svg': { fontSize: { xs: 20, sm: 23 } },
            }}>
              {s.icon}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{
                fontSize: { xs: 12, sm: 13 }, fontWeight: 600, lineHeight: 1.3,
                color: highlight ? 'rgba(255,255,255,.85)' : '#64748b',
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              }}>
                {s.label}
              </Typography>
              <Typography sx={{
                fontSize: { xs: '1.05rem', sm: '1.3rem' }, fontWeight: 800, lineHeight: 1.35,
                direction: 'ltr', textAlign: 'right', fontVariantNumeric: 'tabular-nums',
                color: highlight ? '#fff' : '#0f172a',
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              }}>
                {s.value}
              </Typography>
            </Box>
          </Paper>
        );
      })}
    </Box>
  );
}

// ───────────────────────── الفلاتر ─────────────────────────
// شريط واحد مضغوط: الفترة + التاريخان + فلاتر التقرير + البحث + الأزرار
// على الشاشات الكبيرة صف واحد، وعلى الصغيرة يلتف تلقائيًا، وعلى الجوال يُطوى.
/**
 * @param range             ناتج useReportDateRange
 * @param search / onSearchChange / searchPlaceholder   البحث السريع (اختياري)
 * @param onApply           زر "عرض" (أيقونة)
 * @param onReset           زر "إعادة تعيين" (أيقونة)
 * @param loading
 * @param activeCount       عدد الفلاتر المفعّلة (يظهر على الجوال عند الطي)
 * @param children          فلاتر إضافية خاصة بالتقرير (مثل ReportSelect)
 */
const FIELD_W = { xs: '1 1 calc(50% - 6px)', md: '1 1 150px' };

export function ReportFilters({
  range, search, onSearchChange, searchPlaceholder = 'بحث...',
  onApply, onReset, loading = false, activeCount = 0, children,
}) {
  const isMobile = useMediaQuery('(max-width:600px)');
  const [open, setOpen] = useState(false);
  const show = !isMobile || open;

  return (
    <Paper elevation={0} sx={{
      mb: 1.5, borderRadius: '14px', border: `1px solid ${BORDER}`,
      bgcolor: '#fff', overflow: 'hidden', boxShadow: '0 1px 2px rgba(15,23,42,.04)',
    }}>
      {/* على الجوال فقط: شريط يطوي الفلاتر ويعرض الفترة الحالية */}
      {isMobile && (
        <Box
          onClick={() => setOpen(o => !o)}
          sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1,
            px: 1.5, py: 1.1, cursor: 'pointer', borderBottom: open ? `1px solid ${BORDER}` : 'none',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            <FilterAltOutlinedIcon sx={{ fontSize: 20, color: REPORT_COLORS.primary.main }} />
            <Typography sx={{ fontWeight: 800, fontSize: 14 }}>الفلاتر</Typography>
            {activeCount > 0 && (
              <Chip size="small" label={activeCount}
                sx={{ height: 20, fontSize: 12, fontWeight: 800, bgcolor: REPORT_COLORS.primary.soft, color: REPORT_COLORS.primary.main }} />
            )}
            {range && !open && (
              <Typography sx={{ fontSize: 12.5, color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                · {range.label}
              </Typography>
            )}
          </Box>
          <ExpandMoreIcon sx={{ transition: 'transform .2s', transform: open ? 'rotate(180deg)' : 'none', color: '#64748b' }} />
        </Box>
      )}

      <Collapse in={show} timeout="auto">
        <Box sx={{
          p: { xs: 1.5, md: 1.25 },
          display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.25,
        }}>
          {range && (
            <>
              {/* الفترات الجاهزة كقائمة منسدلة */}
              <TextField
                select size="small" label="الفترة"
                value={range.activePreset ?? 'custom'}
                onChange={(e) => e.target.value !== 'custom' && range.applyPreset(e.target.value)}
                sx={{ ...fieldSx, flex: FIELD_W, '& .MuiSelect-select': { fontWeight: 700, color: REPORT_COLORS.primary.main } }}
                SelectProps={{ MenuProps: { PaperProps: { sx: { borderRadius: '12px', mt: .5 } } } }}
              >
                {DATE_PRESETS.map(p => (
                  <MenuItem key={p.key} value={p.key} sx={{ gap: 1, fontWeight: range.activePreset === p.key ? 800 : 500 }}>
                    {p.label}
                    {range.activePreset === p.key && <CheckRoundedIcon sx={{ fontSize: 17, color: REPORT_COLORS.primary.main, mr: 'auto' }} />}
                  </MenuItem>
                ))}
                <MenuItem value="custom" disabled>فترة مخصصة</MenuItem>
              </TextField>

              <TextField
                label="من تاريخ" type="date" size="small"
                value={range.dateFrom} onChange={(e) => range.setDateFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ lang: 'en', max: range.dateTo }}
                sx={{ ...dateInputSx, flex: FIELD_W }}
              />
              <TextField
                label="إلى تاريخ" type="date" size="small"
                value={range.dateTo} onChange={(e) => range.setDateTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ lang: 'en', min: range.dateFrom }}
                sx={{ ...dateInputSx, flex: FIELD_W }}
              />
            </>
          )}

          {React.Children.map(children, (child) =>
            child ? <Box sx={{ flex: FIELD_W, minWidth: 0 }}>{child}</Box> : null
          )}

          {onSearchChange && (
            <TextField
              size="small"
              value={search ?? ''} onChange={(e) => onSearchChange(e.target.value)}
              placeholder={searchPlaceholder}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start"><SearchIcon sx={{ fontSize: 20, color: '#94a3b8' }} /></InputAdornment>
                ),
              }}
              sx={{ ...fieldSx, flex: { xs: '1 1 100%', md: '2 1 220px' } }}
            />
          )}

          {(onApply || onReset) && (
            <Box sx={{ display: 'flex', gap: .75, flex: { xs: '1 1 100%', md: '0 0 auto' } }}>
              {onReset && (
                <Tooltip title="إعادة تعيين">
                  <IconButton
                    onClick={onReset}
                    sx={{
                      width: 40, height: 40, borderRadius: '10px', color: '#475569',
                      border: `1px solid ${BORDER}`, bgcolor: '#fff', '&:hover': { bgcolor: '#f8fafc' },
                    }}
                  >
                    <RestartAltIcon sx={{ fontSize: 21 }} />
                  </IconButton>
                </Tooltip>
              )}
              {onApply && (
                <Button
                  onClick={onApply}
                  disabled={loading}
                  variant="contained"
                  disableElevation
                  startIcon={loading ? <CircularProgress size={15} color="inherit" /> : <SearchIcon />}
                  sx={{
                    flex: { xs: 1, md: 'none' }, height: 40, px: 2.25, borderRadius: '10px', fontWeight: 700, whiteSpace: 'nowrap',
                    bgcolor: REPORT_COLORS.primary.main, '&:hover': { bgcolor: BRAND.primaryDark },
                    '& .MuiButton-startIcon': { ml: .6, mr: -.25 },
                  }}
                >
                  عرض
                </Button>
              )}
            </Box>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
}

// ───────────────────────── قائمة اختيار موحّدة ─────────────────────────
/**
 * @param options  [{ value, label }]
 * @param allLabel نص خيار "الكل" — مرّر null لإخفائه
 */
export function ReportSelect({ label, value, onChange, options = [], allLabel = 'الكل', ...rest }) {
  return (
    <TextField
      select size="small" fullWidth label={label}
      value={value ?? ''} onChange={(e) => onChange(e.target.value)}
      sx={fieldSx}
      SelectProps={{ MenuProps: { PaperProps: { sx: { borderRadius: '12px', mt: .5 } } } }}
      {...rest}
    >
      {allLabel !== null && <MenuItem value="">{allLabel}</MenuItem>}
      {options.map(o => (
        <MenuItem key={String(o.value)} value={o.value}>{o.label}</MenuItem>
      ))}
    </TextField>
  );
}

// ───────────────────────── إطار الجدول ─────────────────────────
export function ReportTableCard({ title, count, children, height = { xs: '62vh', md: '64vh' } }) {
  return (
    <Paper elevation={0} sx={{
      borderRadius: '16px', border: `1px solid ${BORDER}`, overflow: 'hidden',
      bgcolor: '#fff', boxShadow: '0 1px 2px rgba(15,23,42,.04)',
    }}>
      {(title || count != null) && (
        <Box sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          px: 2, py: 1.25, borderBottom: `1px solid ${BORDER}`, bgcolor: '#fafbfc',
        }}>
          <Typography sx={{ fontWeight: 800, fontSize: 15 }}>{title}</Typography>
          {count != null && (
            <Typography sx={{ fontSize: 13, color: '#64748b', fontWeight: 600 }}>
              {count} سجل
            </Typography>
          )}
        </Box>
      )}
      <Box sx={{ height, minHeight: 380, width: '100%' }}>{children}</Box>
    </Paper>
  );
}

// تنسيق الجدول الموحّد لـ DataGrid
export const reportGridSx = {
  border: 'none',
  fontSize: 14,
  '& .MuiDataGrid-columnHeaders': { bgcolor: '#f8fafc', borderBottom: `1px solid ${BORDER}` },
  '& .MuiDataGrid-columnHeader': { bgcolor: '#f8fafc' },
  '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 800, color: '#334155', fontSize: 13 },
  '& .MuiDataGrid-cell': { borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  '& .MuiDataGrid-row:hover': { bgcolor: '#f8faff' },
  '& .MuiDataGrid-row:nth-of-type(even)': { bgcolor: '#fcfcfd' },
  '& .MuiDataGrid-toolbarContainer': { p: 1.25, gap: 1, flexWrap: 'wrap', borderBottom: `1px solid ${BORDER}` },
  '& .MuiDataGrid-footerContainer': {
    borderTop: `1px solid ${BORDER}`, bgcolor: '#fafbfc',
    '& .MuiTablePagination-displayedRows': { direction: 'ltr', unicodeBidi: 'embed' },
  },
};
